import { useTheme } from '@/context/ThemeContext';
import { listingLifecycle } from '@/mocks/fizboData';

interface IlanYasamDongusuProps {
  daysOld: number;
  createdAt: string;
  status: string;
}

const eventIcons: Record<string, { icon: string; color: string; bg: string }> = {
  created:   { icon: 'ri-add-circle-line',    color: 'text-green-500',  bg: 'bg-green-100 dark:bg-green-900/30' },
  call:      { icon: 'ri-phone-line',          color: 'text-sky-500',    bg: 'bg-sky-100 dark:bg-sky-900/30' },
  price:     { icon: 'ri-price-tag-3-line',    color: 'text-amber-500',  bg: 'bg-amber-100 dark:bg-amber-900/30' },
  meeting:   { icon: 'ri-user-voice-line',     color: 'text-violet-500', bg: 'bg-violet-100 dark:bg-violet-900/30' },
  photo:     { icon: 'ri-camera-line',         color: 'text-pink-500',   bg: 'bg-pink-100 dark:bg-pink-900/30' },
  portfolio: { icon: 'ri-building-line',       color: 'text-primary-500',bg: 'bg-primary-100 dark:bg-primary-900/30' },
  closed:    { icon: 'ri-close-circle-line',   color: 'text-red-500',    bg: 'bg-red-100 dark:bg-red-900/30' },
};

export default function IlanYasamDongusu({ daysOld, createdAt, status }: IlanYasamDongusuProps) {
  const { isDark } = useTheme();

  const totalDays = daysOld;
  const phases = [
    { label: 'Kayıt', days: 0, pct: 0 },
    { label: 'İletişim', days: Math.round(totalDays * 0.2), pct: 20 },
    { label: 'Görüşme', days: Math.round(totalDays * 0.5), pct: 50 },
    { label: 'Teklif', days: Math.round(totalDays * 0.8), pct: 80 },
    { label: 'Kapanış', days: totalDays, pct: 100 },
  ];

  const currentPct = status === 'Kabul' ? 80 : status === 'Düşünecek' ? 50 : status === 'Red' ? 100 : 20;

  return (
    <div className="space-y-5">
      {/* Progress Bar */}
      <div className={`rounded-xl p-4 ${isDark ? 'bg-dark-bg' : 'bg-light-bg'}`}>
        <div className="flex items-center justify-between mb-3">
          <p className={`text-xs font-semibold ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
            İlan Yaşam Döngüsü
          </p>
          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
            status === 'Kabul' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
            status === 'Red' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
            'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
          }`}>
            {status}
          </span>
        </div>

        {/* Phase Labels */}
        <div className="flex justify-between mb-1.5">
          {phases.map((p) => (
            <span key={p.label} className={`text-xs ${p.pct <= currentPct ? 'text-primary-500 font-semibold' : isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
              {p.label}
            </span>
          ))}
        </div>

        {/* Progress Track */}
        <div className={`relative w-full h-3 rounded-full ${isDark ? 'bg-dark-border' : 'bg-light-border'}`}>
          <div
            className="h-full rounded-full bg-primary-500 transition-all duration-1000"
            style={{ width: `${currentPct}%` }}
          />
          {phases.map((p) => (
            <div
              key={p.label}
              className={`absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full border-2 border-white dark:border-dark-bg transition-all ${
                p.pct <= currentPct ? 'bg-primary-500' : isDark ? 'bg-dark-border' : 'bg-light-border'
              }`}
              style={{ left: `calc(${p.pct}% - 6px)` }}
            />
          ))}
        </div>

        {/* Stats Row */}
        <div className="flex items-center justify-between mt-3">
          <div className="text-center">
            <p className={`text-lg font-bold ${isDark ? 'text-dark-text' : 'text-light-text'}`}>{daysOld}</p>
            <p className={`text-xs ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>Aktif Gün</p>
          </div>
          <div className="text-center">
            <p className={`text-lg font-bold ${isDark ? 'text-dark-text' : 'text-light-text'}`}>{createdAt}</p>
            <p className={`text-xs ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>Açılış Tarihi</p>
          </div>
          <div className="text-center">
            <p className={`text-lg font-bold ${isDark ? 'text-dark-text' : 'text-light-text'}`}>{listingLifecycle.length}</p>
            <p className={`text-xs ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>Toplam Olay</p>
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div>
        <p className={`text-xs font-semibold mb-3 uppercase tracking-wide ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
          Olay Zaman Çizelgesi
        </p>
        <div className="relative">
          {/* Vertical line */}
          <div className={`absolute left-4 top-0 bottom-0 w-px ${isDark ? 'bg-dark-border' : 'bg-light-border'}`} />

          <div className="space-y-3">
            {listingLifecycle.map((event, idx) => {
              const style = eventIcons[event.type] || eventIcons.created;
              return (
                <div key={idx} className="flex items-start gap-3 pl-1">
                  {/* Icon */}
                  <div className={`relative z-10 w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${style.bg}`}>
                    <i className={`${style.icon} text-xs ${style.color}`} />
                  </div>
                  {/* Content */}
                  <div className={`flex-1 rounded-xl p-3 ${isDark ? 'bg-dark-bg' : 'bg-light-bg'}`}>
                    <div className="flex items-center justify-between mb-0.5">
                      <p className={`text-xs font-semibold ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
                        {event.event}
                      </p>
                      <span className={`text-xs ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
                        {event.date}
                      </span>
                    </div>
                    <p className={`text-xs ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
                      {event.detail}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Upcoming */}
      <div className={`rounded-xl p-4 border-l-4 border-primary-500 ${isDark ? 'bg-dark-bg' : 'bg-light-bg'}`}>
        <p className={`text-xs font-semibold mb-1 ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
          Sonraki Önerilen Adım
        </p>
        <p className={`text-xs ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
          {status === 'Kabul'
            ? 'Yetki sözleşmesi imzalanması için randevu oluşturun.'
            : status === 'Düşünecek'
            ? 'Mal sahibine 2 gün içinde takip araması yapın.'
            : 'Alternatif teklifle yeniden iletişime geçin.'}
        </p>
      </div>
    </div>
  );
}
